<template>
  <a-menu
    v-model:selectedKeys="selectedKeys"
    v-model:openKeys="openKeys"
    mode="inline"
    theme="dark"
    :inline-collapsed="props.collapsed"
    :items="antdMenuItems"
    @click="handleMenuClick"
    @openChange="handleOpenChange"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, h } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { type MenuProps } from 'ant-design-vue'
import type { ItemType } from 'ant-design-vue/es/menu'
import { sortMenuItems, menuToMenuItems, type MenuItem } from '@/utils/menuUtils'
import { useUserStore } from '@/stores/user'
import { MenuOutlined } from '@ant-design/icons-vue'
import * as AntIcons from '@ant-design/icons-vue'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

defineOptions({
  name: 'SideMenu',
})

const props = defineProps<{
  collapsed: boolean
}>()

const selectedKeys = ref<string[]>([])
const openKeys = ref<string[]>([])

const sortedMenuItems = computed(() => {
  const menuItems = menuToMenuItems(userStore.userPrem.menus)
  return sortMenuItems(menuItems)
})

const resolveIcon = (icon?: string) => {
  if (icon && (AntIcons as Record<string, unknown>)[icon]) {
    return () => h((AntIcons as Record<string, unknown>)[icon] as object)
  }
  return () => h(MenuOutlined)
}

/** 转为 Ant Design Menu items，支持多级菜单。 */
const toAntdItems = (menus: MenuItem[], isRoot = true): ItemType[] => {
  return menus.map((item) => {
    const menuItem = {
      key: item.name,
      label: item.meta.title,
      icon: isRoot ? resolveIcon(item.meta.icon) : undefined,
    } as ItemType

    if (item.children?.length) {
      return {
        ...menuItem,
        children: toAntdItems(item.children, false),
      } as ItemType
    }

    return menuItem
  })
}

const antdMenuItems = computed(() => toAntdItems(sortedMenuItems.value))

const findMenuByName = (menus: MenuItem[], targetName: string): MenuItem | null => {
  for (const menu of menus) {
    if (menu.name === targetName) return menu
    if (menu.children?.length) {
      const found = findMenuByName(menu.children, targetName)
      if (found) return found
    }
  }
  return null
}

const findMenuByPath = (menus: MenuItem[], targetPath: string): MenuItem | null => {
  for (const menu of menus) {
    if (menu.path === targetPath && !menu.children?.length) return menu
    if (menu.children?.length) {
      const found = findMenuByPath(menu.children, targetPath)
      if (found) return found
    }
  }
  return null
}

const findAncestorNames = (
  menus: MenuItem[],
  targetName: string,
  trail: string[] = []
): string[] | null => {
  for (const menu of menus) {
    if (menu.name === targetName) return trail
    if (menu.children?.length) {
      const found = findAncestorNames(menu.children, targetName, [...trail, menu.name])
      if (found) return found
    }
  }
  return null
}

// 一级菜单互斥展开，子菜单保留 Ant Design 的默认展开行为。
const rootSubmenuKeys = computed(() =>
  sortedMenuItems.value.filter((item) => item.children?.length).map((item) => item.name)
)

const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
  const rootKeys = new Set(rootSubmenuKeys.value)
  const latestRootKey = keys.find((key) => rootKeys.has(key) && !openKeys.value.includes(key))

  if (latestRootKey) {
    openKeys.value = [latestRootKey, ...keys.filter((key) => !rootKeys.has(key))]
    return
  }

  openKeys.value = keys
}

const ensureOpenAncestors = (targetName: string) => {
  const ancestors = findAncestorNames(sortedMenuItems.value, targetName) || []
  if (!ancestors.length) return

  const rootKeys = new Set(rootSubmenuKeys.value)
  const latestRootKey = ancestors.find((key) => rootKeys.has(key))
  const otherOpenKeys = openKeys.value.filter((key) => !rootKeys.has(key))
  openKeys.value = latestRootKey
    ? [latestRootKey, ...otherOpenKeys.filter((key) => ancestors.includes(key))]
    : Array.from(new Set([...openKeys.value, ...ancestors]))
}

const handleMenuClick: MenuProps['onClick'] = async ({ key }) => {
  const name = String(key)
  const menu = findMenuByName(sortedMenuItems.value, name)

  // 目录节点只负责展开，不触发路由跳转。
  if (menu?.children?.length) return

  if (router.hasRoute(name)) {
    await router.push({ name })
  } else if (menu?.path?.startsWith('/')) {
    await router.push(menu.path)
  }

  await nextTick()
  ensureOpenAncestors(name)
}

const updateMenuStatus = () => {
  const routeName = (route.name as string) || ''
  let selectedName = routeName

  if (routeName && !findMenuByName(sortedMenuItems.value, routeName)) {
    const byPath = findMenuByPath(sortedMenuItems.value, route.path)
    if (byPath) selectedName = byPath.name
  }

  selectedKeys.value = selectedName ? [selectedName] : []
  if (selectedName) ensureOpenAncestors(selectedName)
}

watch(() => route.fullPath, updateMenuStatus)
watch(
  () => userStore.userPrem.menus,
  () => nextTick(updateMenuStatus),
  { deep: true }
)

updateMenuStatus()
</script>

<style scoped lang="less">
/* 与设备管理等既有菜单一致：未选中灰、选中偏白 */
:deep(.ant-menu.ant-menu-dark) {
  .ant-menu-item,
  .ant-menu-submenu-title {
    color: rgba(255, 255, 255, 0.65);
  }

  .ant-menu-item:hover,
  .ant-menu-submenu-title:hover {
    color: #fff;
  }

  .ant-menu-item-selected,
  .ant-menu-item-selected:hover {
    color: #fff;
  }

  .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: rgba(255, 255, 255, 0.65);
  }
}
</style>
